"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

/**
 * Top-level primary-nav link with an active-page indicator. Client-side
 * so it can read the current pathname: the active item (or its section)
 * gets the accent underline + full-strength label. Hover underline is
 * handled by the passed `className` (CSS), so this only adds the active
 * state on top.
 */
export function Ci926NavLink({
  href,
  className,
  children,
  target,
  rel,
  activeColor = "#ffffff",
}: {
  href: string;
  className: string;
  children: React.ReactNode;
  target?: string;
  rel?: string;
  /** Label colour of the active item — white on dark bars, primary on light. */
  activeColor?: string;
}) {
  const pathname = usePathname() || "/";
  const clean = href && href !== "#" ? href.split("?")[0].replace(/\/$/, "") || "/" : "";
  // Root links ("/" and a department home "/dept/<slug>") are active only on
  // that exact page — otherwise "Home" stays lit on every sub-page.
  const isRoot = clean === "/" || /^\/dept\/[^/]+$/.test(clean);
  const active =
    !!clean &&
    (isRoot
      ? pathname.replace(/\/$/, "") === clean.replace(/\/$/, "") || (clean === "/" && pathname === "/")
      : pathname === clean || pathname.startsWith(clean + "/"));

  return (
    <Link
      href={href}
      target={target}
      rel={rel}
      aria-current={active ? "page" : undefined}
      className={className}
      style={
        active
          ? { borderBottomColor: "var(--color-accent, #FFC63B)", color: activeColor }
          : undefined
      }
    >
      {children}
    </Link>
  );
}

export default Ci926NavLink;
